/**
 * JavaScript mirror of tokens.css.
 *
 * Use sparingly — prefer CSS custom properties via `var(--token-name)`.
 * This mirror exists for the occasional inline-style value that genuinely
 * needs a TS-side constant (e.g. an animation-frame-driven width, or a
 * value passed into a non-CSS context such as a canvas).
 *
 * Source of truth is `tokens.css`. Keep these in sync — if you find
 * yourself reaching for a token here that isn't mirrored, consider whether
 * the consumer should be using `var(--token)` instead before extending
 * this file.
 */

/** Spacing scale (4 px units). Mirrors `--space-N`. */
export const space = {
  0: '0',
  1: '0.25rem', // 4 px
  2: '0.5rem', // 8 px
  3: '0.75rem', // 12 px
  4: '1rem', // 16 px
  5: '1.25rem', // 20 px
  6: '1.5rem', // 24 px
  8: '2rem', // 32 px
  10: '2.5rem', // 40 px
  12: '3rem', // 48 px
} as const;

/** Type scale. Mirrors `--font-{xs,sm,md,lg,xl,2xl}`. */
export const fontSize = {
  xs: '0.75rem', // 12 px
  sm: '0.8125rem', // 13 px
  md: '0.9375rem', // 15 px
  lg: '1.125rem', // 18 px
  xl: '1.375rem', // 22 px
  '2xl': '1.75rem', // 28 px — page titles
} as const;

/** Font weights. Mirrors `--font-weight-*`. */
export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

/** Line heights. Mirrors `--line-*`. */
export const lineHeight = {
  tight: 1.2,
  normal: 1.5,
} as const;

/** Radius scale. Mirrors `--radius-*`. */
export const radius = {
  sm: '4px',
  md: '6px',
  lg: '10px',
  pill: '999px',
} as const;

/**
 * Status colour family. Light-mode values; dark-mode overrides are applied
 * via `html.dark` in tokens.css. For runtime values prefer reading the CSS
 * custom property (`var(--status-online-accent)`) so theme changes are
 * picked up automatically; this object exists for the rare case (e.g.
 * canvas drawing) where a literal hex is genuinely required.
 */
export const status = {
  online: { accent: '#1FB87A', bg: 'rgba(31, 184, 122, 0.10)' },
  warning: { accent: '#E0A82E', bg: 'rgba(224, 168, 46, 0.10)' },
  error: { accent: '#E54848', bg: 'rgba(229, 72, 72, 0.10)' },
  info: { accent: '#2563EB', bg: 'rgba(37, 99, 235, 0.10)' },
  neutral: { accent: '#6B7280', bg: 'rgba(107, 114, 128, 0.10)' },
} as const;

/** Motion. Mirrors `--motion-*`. */
export const motion = {
  fast: '120ms cubic-bezier(0.4, 0, 0.2, 1)',
  normal: '180ms cubic-bezier(0.4, 0, 0.2, 1)',
  slow: '320ms cubic-bezier(0.4, 0, 0.2, 1)',
} as const;
